import React from 'react';
import { compact, forEach, has, isEmpty, isPlainObject, isUndefined, map, omit } from 'lodash';
import UIUtils from 'js/lib/UIUtils';
import { slugify } from 'new/utils/slugify';
import { FieldBuilderRedesigned } from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import { NexlaAccordion } from 'src/new/components/NexlaAccordion';
import { Field } from '@nexla/@types';
import { ModalDefault } from 'new/routes/ModalDefault';
import { Button, CircularProgress, Stack } from '@mui/material';

import styles from './CredentialModalForm.module.scss';

function shouldLookForOptionalFields(fields = [], isCredentialsForm = false) {
  if (!isCredentialsForm) {
    // for the Add Data Source, Configure FormPage's Advanced Settings
    return false;
  }
  return !!fields.find((item) => !item.required && item.id !== 'cred_desc'); // has at least on optional field
}

interface iProps {
  heading: string;
  fields: Field[];
  data: any;
  fieldsMeta: Record<string, string[]>;
  buttonsDisabled: boolean;
  submitButtonLabel?: string;
  customButtons?: any[];
  onFieldUpdated: any;
  onButtonFieldClicked: any;
  rootStyle?: Record<string, string | number>;
  topContent?: React.ReactNode;

  onSubmit(...args: any): void;

  onCloseClicked(): void;
}

interface iState {
  hasOptionalField: boolean;
}

class CredentialModalForm extends React.Component<iProps, iState> {
  constructor(props) {
    super(props);
    this.state = {
      hasOptionalField: shouldLookForOptionalFields(props.fields, props.isCredentialsForm),
    } as any;
    this._renderFields = this._renderFields.bind(this);
    this._renderField = this._renderField.bind(this);
    this._handleFieldUpdated = this._handleFieldUpdated.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    if (!state.hasOptionalField && props.isCredentialsForm) {
      const hasOptionalField = shouldLookForOptionalFields(props.fields, props.isCredentialsForm);
      if (hasOptionalField !== state.hasOptionalField) {
        return {
          hasOptionalField: hasOptionalField,
        };
      }
    }

    return null;
  }

  render() {
    const {
      heading = 'Credential',
      fields,
      fieldsMeta,
      submitButtonLabel = 'Save Credential',
      customButtons,
      buttonsDisabled,
      topContent,
      onSubmit,
      onCloseClicked,
    } = this.props;

    let optionalContent = [];
    let formContent;

    if (fieldsMeta) {
      let fieldsMap = {};
      forEach(fields, (field, ix) => {
        fieldsMap[field.name] = ix;
      });
      if (fieldsMeta.advanced && fieldsMeta.advanced.length) {
        let optionalFields = map(fieldsMeta.advanced, (k) => {
          return fields[fieldsMap[k]];
        });
        optionalContent = this._renderFields(optionalFields);
      }
      let mainFields = map(fieldsMeta.basic, (k) => {
        return fields[fieldsMap[k]];
      });
      formContent = this._renderFields(mainFields);
    } else {
      if (this.state.hasOptionalField) {
        let optionalFields = fields.filter((item) => !item.required && item.name !== 'cred_desc');
        let mainFields = fields.filter((item) => item.required || item.name === 'cred_desc');
        optionalContent = this._renderFields(optionalFields);
        formContent = this._renderFields(mainFields);
      } else {
        formContent = this._renderFields(fields);
      }
    }

    return (
      <ModalDefault
        title={heading}
        maxWidth="lg"
        removeContentPadding
        config={{ disableClickOut: true }}
        rootClassName={styles.modal_root_override}
        onCancel={onCloseClicked}
      >
        <>
          <div className={styles.fields}>
            {topContent}
            {formContent}
            {optionalContent.length ? (
              <div className={styles.optional_fields_card}>
                <NexlaAccordion title="Advanced Settings" children={optionalContent} />
              </div>
            ) : null}
          </div>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            padding="16px"
            borderTop="1px solid var(--color-greyscale-100)"
          >
            <Button variant="outlined" color="info" onClick={onCloseClicked}>
              Cancel
            </Button>
            {customButtons &&
              map(customButtons, (buttonProps, index) => {
                const { style, labelStyle, label, ...otherButtonProps } = buttonProps;
                const buttonStyle = { height: 36, ...style };
                const buttonLabelStyle = { fontSize: 10, ...labelStyle };
                return (
                  <Button
                    key={index}
                    disabled={buttonsDisabled}
                    rootStyle={buttonStyle}
                    {...otherButtonProps}
                  >
                    <span style={buttonLabelStyle}>{label}</span>
                  </Button>
                );
              })}
            <Button
              variant="ghost"
              color="primary"
              onClick={onSubmit}
              disabled={buttonsDisabled}
              startIcon={buttonsDisabled ? <CircularProgress size={20} /> : undefined}
            >
              {submitButtonLabel}
            </Button>
          </Stack>
        </>
      </ModalDefault>
    );
  }

  _renderFields(fields) {
    const fieldsBySection = {};
    const sections = [];

    forEach(fields, (field) => {
      const { section } = field;

      if (!has(fieldsBySection, section)) {
        fieldsBySection[section] = [];

        if (isUndefined(section)) {
          sections.unshift(section);
        } else {
          sections.push(section);
        }
      }

      fieldsBySection[section].push(field);
    });

    const formContent = [];

    forEach(sections, (section) => {
      const sectionFields = compact(map(fieldsBySection[section], this._renderField));

      if (section && !isEmpty(sectionFields)) {
        formContent.push(
          <h2
            id={'section-heading-' + slugify(section)}
            key={'section-heading-' + section}
            className="section-heading"
          >
            {section}
          </h2>,
        );
      }

      formContent.push(
        <div className={styles.form_content} key={'section-fields-' + section}>
          {[...sectionFields]}
        </div>,
      );
    });

    return formContent;
  }

  _renderField(field) {
    const { data = {} } = this.props;

    if (UIUtils.shouldHideField(field, data) === true || !UIUtils.shouldRenderField(field, data)) {
      return null;
    }

    // for button fields, check if it depends on anything
    const defaultFieldValue =
      field.type === 'nonce'
        ? UIUtils.generateNonce()
        : has(field, 'default')
          ? field.default
          : null;
    const currentFieldValue = has(data, field.name) ? data[field.name] : defaultFieldValue;
    const isRequired = field.required && !isPlainObject(field.required) && !has(field, 'default');

    const key = 'grp-' + field.name;

    return (
      <FieldBuilderRedesigned
        key={key}
        id={field.name}
        field={field}
        isRequired={isRequired}
        currentFieldValue={currentFieldValue}
        onChange={this._handleFieldUpdated}
        fieldLabelClassName={styles.input_label}
        inModal={true}
      />
    );
  }

  _handleFieldUpdated = (name, value, isButtonField = false) => {
    const { fields, data, onFieldUpdated, onButtonFieldClicked } = this.props;
    let newData = { ...(data || {}) };

    if (name) {
      newData[name] = value;
    }

    //check if any conditional params are to be rendered. if they are missing in source config add their default value.
    newData = UIUtils.configWithMissingDefaults(newData, fields);
    // omit fields which shouldn't be rendered
    const invisibleFieldNames = fields
      .filter((field) => !UIUtils.shouldRenderField(field, newData))
      .map((field) => field.name);
    const visibleFieldData = omit(newData, invisibleFieldNames);
    if (isButtonField) {
      onButtonFieldClicked(name, value, visibleFieldData);
    } else {
      onFieldUpdated(name, value, visibleFieldData);
    }
  };
}

export default CredentialModalForm;
